/**
 * Composant d'acceptation/refus pour les référents
 * Phase 7 - Écran Acceptation Référent (UX)
 * Dupliqué et adapté de CandidateDecisionForm.tsx
 */

import { useState } from 'react';


import { BrandingSettings } from '@/hooks/useReferentBranding';

interface ReferentDecisionFormProps {
    referent: any;
    campaign: any;
    branding: BrandingSettings | null;
    initialShowRefusalForm?: boolean;
    onAccept: () => void;
    onRefuse: (reason: string) => void;
    isSubmitting: boolean;
}

export const ReferentDecisionForm = ({
    referent,
    campaign,
    branding,
    initialShowRefusalForm = false,
    onAccept,
    onRefuse,
    isSubmitting
}: ReferentDecisionFormProps) => {
    const [showRefusalForm, setShowRefusalForm] = useState(initialShowRefusalForm);
    const [refusalReason, setRefusalReason] = useState('');

    const MIN_REASON_LENGTH = 10;
    const isReasonValid = refusalReason.trim().length >= MIN_REASON_LENGTH;

    const handleRefuseSubmit = () => {
        if (isReasonValid) {
            onRefuse(refusalReason.trim());
        }
    };

    if (showRefusalForm) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 font-['Bricolage_Grotesque']">
                <div className="w-full max-w-[646px] bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
                    {/* Header avec logo et badge */}
                    <div className="self-stretch px-4 sm:px-7 py-6 border-b border-neutral-400/20 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <img
                            className="w-32 sm:w-40 h-auto"
                            src={(branding?.enabled && branding?.logoUrl) ? branding.logoUrl : "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"}
                            alt={(branding?.enabled && branding?.logoUrl) ? "Logo Entreprise" : "Getproof Logo"}
                        />
                        <div
                            className="px-2 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5"
                            style={{
                                outlineColor: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf',
                                color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf'
                            }}
                        >
                            <div className="justify-start text-xs sm:text-sm font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight text-center">
                                Demande de référence professionnelle
                            </div>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="self-stretch px-4 sm:px-7 py-8 flex flex-col justify-end items-center gap-6">
                        {/* Titre et description */}
                        <div className="self-stretch flex flex-col justify-start items-start gap-2">
                            <div className="text-center justify-start text-gray-800 text-xl sm:text-2xl font-medium font-['Bricolage_Grotesque'] leading-7">
                                Refus de participer
                            </div>
                            <div className="self-stretch justify-start text-sm">
                                <span className="text-neutral-400 font-normal font-['Bricolage_Grotesque'] leading-none">
                                    Vous avez choisi de ne pas fournir de référence pour
                                </span>
                                <span className="text-gray-800 font-normal font-['Bricolage_Grotesque'] leading-none">
                                    {campaign.candidate_name}.
                                </span>
                            </div>
                        </div>

                        {/* Section motif du refus */}
                        <div className="w-full flex flex-col justify-start items-start gap-2">
                            <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                                Motif du refus
                            </div>
                            <div className="self-stretch h-28 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-start">
                                <textarea
                                    value={refusalReason}
                                    onChange={(e) => setRefusalReason(e.target.value)}
                                    placeholder="Veuillez indiquer la raison de votre refus de fournir une référence..."
                                    className="flex-1 justify-center text-gray-800 text-xs font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none resize-none h-full"
                                    required
                                />
                            </div>
                            <div className="self-stretch justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                                Ces informations seront transmises au recruteur.
                            </div>
                            {refusalReason.trim() && !isReasonValid && (
                                <div className="self-stretch justify-start text-rose-500 text-xs font-normal font-['Bricolage_Grotesque']">
                                    Minimum {MIN_REASON_LENGTH} caractères requis ({refusalReason.trim().length}/{MIN_REASON_LENGTH})
                                </div>
                            )}
                        </div>

                        {/* Boutons */}
                        <div className="w-full flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => setShowRefusalForm(false)}
                                disabled={isSubmitting}
                                className="flex-1 h-12 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-center items-center gap-1 overflow-hidden hover:bg-gray-50 disabled:opacity-50"
                            >
                                <div className="justify-center text-gray-800 text-sm sm:text-base font-semibold font-['Bricolage_Grotesque']">
                                    Annuler
                                </div>
                            </button>
                            <button
                                onClick={handleRefuseSubmit}
                                disabled={isSubmitting || !isReasonValid}
                                className="flex-1 h-12 p-3 bg-rose-500 rounded-[10px] inline-flex justify-center items-center gap-1 overflow-hidden hover:bg-rose-600 disabled:opacity-50"
                            >
                                <div className="justify-center text-white text-sm sm:text-base font-semibold font-['Bricolage_Grotesque']">
                                    {isSubmitting ? 'Envoi...' : 'Confirmer le refus'}
                                </div>
                            </button>
                        </div>

                        {/* Séparateur */}
                        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>

                        {/* Footer */}
                        <div className="flex flex-col justify-center items-center gap-4">
                            <div className="w-full max-w-96 text-center justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] leading-none">
                                Cette demande de référence est effectuée conformément à la législation en vigueur. Vos réponses sont confidentielles et traitées conformément au RGPD.
                            </div>
                            <div
                                className="text-center justify-center text-sm font-normal font-['Bricolage_Grotesque'] leading-tight"
                                style={{ color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf' }}
                            >
                                L'équipe Getproof
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 font-['Bricolage_Grotesque']">
            <div className="w-full max-w-[646px] bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
                {/* Header avec logo et badge */}
                <div className="self-stretch px-4 sm:px-7 py-6 border-b border-neutral-400/20 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <img
                        className="w-32 sm:w-40 h-auto"
                        src={(branding?.enabled && branding?.logoUrl) ? branding.logoUrl : "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"}
                        alt={(branding?.enabled && branding?.logoUrl) ? "Logo Entreprise" : "Getproof Logo"}
                    />
                    <div
                        className="px-2 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5"
                        style={{
                            outlineColor: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf',
                            color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf'
                        }}
                    >
                        <div className="justify-start text-xs sm:text-sm font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight text-center">
                            Demande de référence professionnelle
                        </div>
                    </div>
                </div>

                {/* Contenu principal */}
                <div className="self-stretch px-4 sm:px-7 py-8 flex flex-col justify-end items-center gap-6">
                    {/* Titre principal */}
                    <div className="self-stretch justify-start text-gray-800 text-xl sm:text-2xl font-medium font-['Bricolage_Grotesque'] leading-7">
                        Prise de référence professionnelle
                    </div>

                    {/* Description */}
                    <div className="self-stretch justify-start text-sm">
                        <span className="text-neutral-400 font-normal font-['Bricolage_Grotesque'] leading-none">
                            {campaign.candidate_name} vous a désigné comme référent pour le poste de
                        </span>
                        <span className="text-gray-800 font-normal font-['Bricolage_Grotesque'] leading-none">
                            {campaign.target_position}.
                        </span>
                    </div>

                    {/* Détails */}
                    <div className="w-full rounded-[10px] flex flex-col justify-start items-start gap-4">
                        <div className="self-stretch justify-start text-gray-800 text-lg sm:text-xl font-medium font-['Bricolage_Grotesque'] leading-normal">
                            Détails de la demande
                        </div>
                        <div className="w-full flex flex-col justify-start items-start gap-4">
                            <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <div className="justify-start text-neutral-400 text-sm sm:text-base font-normal font-['Bricolage_Grotesque'] leading-tight">
                                    Candidat(e):
                                </div>
                                <div className="text-left sm:text-center justify-start text-gray-800 text-sm sm:text-base font-medium font-['Bricolage_Grotesque'] leading-tight">
                                    {campaign.candidate_name}
                                </div>
                            </div>
                            <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <div className="justify-start text-neutral-400 text-sm sm:text-base font-normal font-['Bricolage_Grotesque'] leading-tight">
                                    Poste visé :
                                </div>
                                <div className="text-left sm:text-center justify-start text-gray-800 text-sm sm:text-base font-medium font-['Bricolage_Grotesque'] leading-tight">
                                    {campaign.target_position}
                                </div>
                            </div>
                            <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <div className="justify-start text-neutral-400 text-sm sm:text-base font-normal font-['Bricolage_Grotesque'] leading-tight">
                                    Type de relation:
                                </div>
                                <div className="text-left sm:text-center justify-start text-gray-800 text-sm sm:text-base font-medium font-['Bricolage_Grotesque'] leading-tight">
                                    {referent.relation_type}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cadre légal */}
                    <div className="w-full rounded-[10px] flex flex-col justify-start items-start gap-4">
                        <div className="self-stretch justify-start text-gray-800 text-lg sm:text-xl font-medium font-['Bricolage_Grotesque'] leading-normal">
                            📋 Cadre légal
                        </div>
                        <div className="self-stretch justify-center text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                            Votre participation est volontaire (art. L1221-8 du Code du travail).
                            Vos réponses seront confidentielles et transmises uniquement au recruteur.
                            Les données collectées sont utilisées exclusivement dans le cadre du recrutement
                            et traitées conformément au RGPD.
                        </div>
                    </div>

                    {/* Votre décision */}
                    <div className="w-full rounded-[10px] flex flex-col justify-start items-start gap-4">
                        <div className="w-full flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start text-gray-800 text-lg sm:text-xl font-medium font-['Bricolage_Grotesque'] leading-normal">
                                Votre décision
                            </div>
                            <div className="self-stretch justify-center text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                                Souhaitez-vous fournir une référence pour ce candidat?
                            </div>
                        </div>
                        <div className="self-stretch min-h-20 flex flex-col sm:flex-row justify-start items-stretch gap-4">
                            <button
                                onClick={onAccept}
                                disabled={isSubmitting}
                                className="flex-1 self-stretch p-3 bg-gray-50 rounded-[10px] outline outline-1 outline-offset-[-1px] inline-flex flex-col justify-center items-center gap-1 overflow-hidden hover:opacity-90 disabled:opacity-50"
                                style={{
                                    outlineColor: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf'
                                }}
                            >
                                <div
                                    className="justify-center text-sm font-semibold font-['Bricolage_Grotesque']"
                                    style={{ color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf' }}
                                >
                                    ✅ Participer
                                </div>
                                <div className="w-full max-w-40 justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] text-center">
                                    Évaluer le candidat (2-3 min)
                                </div>
                            </button>
                            <button
                                onClick={() => setShowRefusalForm(true)}
                                disabled={isSubmitting}
                                className="flex-1 self-stretch p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-center items-center gap-1 overflow-hidden hover:bg-gray-50 disabled:opacity-50"
                            >
                                <div className="justify-center text-rose-500 text-sm font-semibold font-['Bricolage_Grotesque']">
                                    ❌ Refuser
                                </div>
                                <div className="w-full max-w-40 text-center justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                                    Ne pas participer
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Séparateur */}
                    <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>

                    {/* Footer */}
                    <div className="flex flex-col justify-center items-center gap-4">
                        <div className="w-full max-w-96 text-center justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] leading-none">
                            Cette demande de référence est effectuée conformément à la législation en vigueur. Vos réponses sont confidentielles et traitées conformément au RGPD.
                        </div>
                        <div
                            className="text-center justify-center text-sm font-normal font-['Bricolage_Grotesque'] leading-tight"
                            style={{ color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf' }}
                        >
                            L'équipe Getproof
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};